import { apiClient } from "../../../client";
import { StandardAxiosConfig, getBaseDataURL } from "../../../common";

/**
 * The scheduling board's three batch partial-writes on Jobs, mirroring the backend sub-router
 * that serves them (`API/src/routers/data/jobs_scheduling.py`). Each maps the domain's
 * `{ jobId, … }` shape onto the wire's `{ id, … }` and POSTs to a dedicated route — a full-Job
 * partial can't go through `/update`, because Job's many required fields would fail validation
 * for an id-plus-one-field payload. `null` clears the field; empty input is a no-op that never
 * touches the network.
 */
async function postBatch(route: string, body: unknown[], failureLabel: string): Promise<void> {
	if (body.length === 0) return;
	const response = await apiClient.post(`${getBaseDataURL("jobs")}/${route}`, body, StandardAxiosConfig());
	if (response.status !== 200) {
		throw new Error(`Failed to update job ${failureLabel}`);
	}
}

/** `Jobs.Order` — the scheduling run-sequence. `order: null` clears it (job unassigned). */
export function postJobOrders(updates: { jobId: string; order: number | null }[]): Promise<void> {
	return postBatch(
		"update-orders",
		updates.map((u) => ({ id: u.jobId, order: u.order })),
		"orders",
	);
}

/** `Jobs."Dropoffs/Shipping"` — which shift-dropoff stop receives each job's samples. */
export function postJobDropoffs(updates: { jobId: string; shiftDropoffId: string | null }[]): Promise<void> {
	return postBatch(
		"update-dropoffs",
		updates.map((u) => ({ id: u.jobId, shiftDropoffId: u.shiftDropoffId })),
		"dropoffs",
	);
}

/** `Jobs.Location` — the cluster pull-in point. Provenance (`Lat Lon Source`) is stamped by the
 *  endpoint, never sent by the caller. */
export function postJobLocations(updates: { jobId: string; location: string | null }[]): Promise<void> {
	return postBatch(
		"update-locations",
		updates.map((u) => ({ id: u.jobId, location: u.location })),
		"locations",
	);
}
